import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { getDefaultPayCurrency } from '../api/workspaceBranding';

const CURRENCY_INSIGNIA = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
};

const CurrencyDisplayContext = createContext(null);

export function currencyInsignia(code) {
  const normalized = String(code || 'EUR').toUpperCase();
  return CURRENCY_INSIGNIA[normalized] || normalized;
}

export function displayCurrencyInsignia(code) {
  return currencyInsignia(code || 'EUR');
}

export function selectionToCurrency(selection) {
  if (!selection || selection === 'ALL') {
    return { displayCurrency: 'EUR', currency: null, selection: 'ALL' };
  }
  if (selection === 'EUR') {
    return { displayCurrency: 'EUR', currency: 'EUR', selection: 'EUR' };
  }
  return { displayCurrency: 'NATIVE', currency: selection, selection };
}

function selectionFromDefault(code) {
  if (!code || String(code).toUpperCase() === 'EUR') return 'ALL';
  return String(code).toUpperCase();
}

export function CurrencyDisplayProvider({
  children,
  initialSelection = 'ALL',
  defaultSelection = 'ALL',
}) {
  const [selection, setSelection] = useState(initialSelection);
  const value = useMemo(
    () => ({
      ...selectionToCurrency(selection),
      setSelection,
      defaultSelection,
    }),
    [selection, defaultSelection]
  );

  return React.createElement(CurrencyDisplayContext.Provider, { value }, children);
}

CurrencyDisplayProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialSelection: PropTypes.string,
  defaultSelection: PropTypes.string,
};

export function CompanyCurrencyProvider({ children }) {
  const location = useLocation();
  const loadedForToken = useRef(null);
  const [selection, setSelection] = useState('ALL');
  const [defaultSelection, setDefaultSelection] = useState('ALL');
  const [ready, setReady] = useState(() => !localStorage.getItem('token'));

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      loadedForToken.current = null;
      setSelection('ALL');
      setDefaultSelection('ALL');
      setReady(true);
      return undefined;
    }
    if (loadedForToken.current === token) return undefined;
    let cancelled = false;
    getDefaultPayCurrency()
      .then((code) => {
        if (cancelled) return undefined;
        const next = selectionFromDefault(code);
        setSelection(next);
        setDefaultSelection(next);
        loadedForToken.current = token;
        return undefined;
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const value = useMemo(
    () => ({
      ...selectionToCurrency(selection),
      setSelection,
      defaultSelection,
      setDefaultSelection,
    }),
    [selection, defaultSelection]
  );

  if (!ready) return null;
  return React.createElement(CurrencyDisplayContext.Provider, { value }, children);
}

CompanyCurrencyProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const FALLBACK = {
  ...selectionToCurrency('ALL'),
  defaultSelection: 'ALL',
  setSelection() {},
  setDefaultSelection() {},
};

export default function useCurrencyDisplay() {
  return useContext(CurrencyDisplayContext) || FALLBACK;
}
